import React, { useEffect } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import FalconCloseButton from "components/common/FalconCloseButton";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { handleFillStepData } from "../config/claimSlice";

import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import MultiSelect from "components/common/MultiSelect";
import BootCheckbox from "components/common/custom/forms/BootCheckbox";

const EditMedicalInformationModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const { stepFormData } = useSelector((state) => state.claim);
  const { missingTeethList, missingTeethListReason } = useSelector(
    (state) => state.preauthorization
  );
  const {
    handleSubmit,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      msv_ref_no: "",
      chief_complaint: "",
      treatment_plan: "",
      physical_examination: "",
      patient_history: "",
      history_present_illness: "",
      significant_sign: "",
      clinical_data: "",
      other_condition: "",
      temperature: 34.5,
      missing_tooth: false,
      missing_tooth_number: null,
      missing_tooth_date: null,
      missing_reason: null,
    },
  });

  const missingTooth = watch("missing_tooth");

  useEffect(() => {
    if (show) {
      setValue("chief_complaint", stepFormData?.chief_complaint);
      setValue("treatment_plan", stepFormData?.treatment_plan);
      setValue("physical_examination", stepFormData?.physical_examination);
      setValue("patient_history", stepFormData?.patient_history);
      setValue(
        "history_present_illness",
        stepFormData?.history_present_illness
      );
      setValue("significant_sign", stepFormData?.significant_sign);
      setValue("clinical_data", stepFormData?.clinical_data);
      setValue("other_condition", stepFormData?.other_condition);
      setValue("missing_tooth", stepFormData?.missing_tooth);
      setValue("missing_tooth_date", stepFormData?.missing_tooth_date);
      setValue("missing_tooth_number", stepFormData?.missing_tooth_number);
      setValue("missing_reason", stepFormData?.missing_reason);
    }
  }, [show]);

  const handleFormSubmit = (data) => {
    dispatch(
      handleFillStepData({
        check: "chief_complaint",
        value: data?.chief_complaint,
      })
    );
    dispatch(
      handleFillStepData({
        check: "treatment_plan",
        value: data?.treatment_plan,
      })
    );
    dispatch(
      handleFillStepData({
        check: "physical_examination",
        value: data?.physical_examination,
      })
    );
    dispatch(
      handleFillStepData({
        check: "patient_history",
        value: data?.patient_history,
      })
    );
    dispatch(
      handleFillStepData({
        check: "history_present_illness",
        value: data?.history_present_illness,
      })
    );
    dispatch(
      handleFillStepData({
        check: "clinical_data",
        value: data?.clinical_data,
      })
    );
    dispatch(
      handleFillStepData({
        check: "significant_sign",
        value: data?.significant_sign,
      })
    );
    dispatch(
      handleFillStepData({
        check: "other_condition",
        value: data?.other_condition,
      })
    );
    dispatch(
      handleFillStepData({
        check: "missing_tooth",
        value: data?.missing_tooth,
      })
    );
    dispatch(
      handleFillStepData({
        check: "missing_tooth_date",
        value: data?.missing_tooth ? data?.missing_tooth_date : null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "missing_tooth_number",
        value: data?.missing_tooth ? data?.missing_tooth_number : null,
      })
    );
    dispatch(
      handleFillStepData({
        check: "missing_reason",
        value: data?.missing_tooth ? data?.missing_reason : null,
      })
    );

    reset(); // Reset form after submission
    onHide(); // Close modal
  };

  return (
    <Modal show={show} size="xl" centered>
      <Modal.Header>
        <Modal.Title className="fs-10">Medical Information</Modal.Title>
        <FalconCloseButton
          onClick={() => {
            reset();
            onHide();
          }}
          size="sm"
          className="position-absolute top-0 end-0 me-2 mt-2"
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(handleFormSubmit)}>
          <Row>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="chief_complaint"
                control={control}
                required
                rules={{ required: "Chief Complaint is required" }}
                size="sm"
                multiline
                rows={3}
                label="Chief Complaint"
                placeholder="Chief Complaint"
              />
            </Col>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="treatment_plan"
                control={control}
                required
                rules={{ required: "Treatment Plan is required" }}
                size="sm"
                multiline
                rows={3}
                label="Treatment Plan"
                placeholder="Treatment Plan"
              />
            </Col>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="physical_examination"
                control={control}
                required
                rules={{ required: "Physical Examination is required" }}
                size="sm"
                multiline
                rows={3}
                label="Physical Examination"
                placeholder="Physical Examination"
              />
            </Col>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="patient_history"
                control={control}
                required
                rules={{ required: "Patient History is required" }}
                size="sm"
                multiline
                rows={3}
                label="Patient History"
                placeholder="Patient History"
              />
            </Col>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="history_present_illness"
                control={control}
                required
                rules={{ required: "History of Present Illness is required" }}
                size="sm"
                multiline
                rows={3}
                label="History Of Present Illness"
                placeholder="History Of Present Illness"
              />
            </Col>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="significant_sign"
                control={control}
                size="sm"
                multiline
                rows={3}
                label="Significant Sign"
                placeholder="Significant Sign"
              />
            </Col>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="clinical_data"
                control={control}
                size="sm"
                multiline
                rows={3}
                label="Clinical Data"
                placeholder="Clinical Data"
              />
            </Col>
            <Col xs={12} md={6} xl={4}>
              <TextInput
                name="other_condition"
                control={control}
                size="sm"
                multiline
                rows={3}
                label="Other Condition"
                placeholder="Other Condition"
              />
            </Col>
            <Col xs={12}>
              <Row className="align-items-center">
                <Col xs={12} sm={12} xl={6}>
                  <Row className="align-items-center">
                    <Col xs={6} md={7}>
                      <h5 className="fs-10 mb-0 mt-2">Missing Tooth?</h5>
                    </Col>
                    <Col xs={6} md={5}>
                      <BootCheckbox
                        name="missing_tooth"
                        label=""
                        control={control}
                        size="lg"
                      />
                    </Col>
                  </Row>
                </Col>

                {missingTooth && (
                  <>
                    <Col xs={12} sm={6} xl={2} className="mt-2">
                      <Controller
                        name="missing_tooth_number"
                        control={control}
                        rules={{ required: missingTooth }}
                        render={({ field }) => (
                          <MultiSelect
                            {...field}
                            isMulti={false}
                            isClearable
                            menuPlacement="auto"
                            closeMenuOnSelect={false}
                            placeholder="Select"
                            value={
                              field.value
                                ? {
                                    value: field.value.oral_code,
                                    label: field.value.oralc_description,
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              field.onChange(
                                selected
                                  ? {
                                      oral_code: selected.value,
                                      oralc_description: selected.label,
                                    }
                                  : null
                              )
                            }
                            options={missingTeethList?.map((item) => ({
                              value: item?.oral_code,
                              label: item?.oralc_description,
                            }))}
                          />
                        )}
                      />
                    </Col>

                    <Col xs={12} sm={6} xl={2} className="mt-2">
                      <BootDatePicker
                        name="missing_tooth_date"
                        label=""
                        size="sm"
                        control={control}
                        isRequired={missingTooth}
                        rules={{ required: missingTooth }}
                        placeholder="dd-mm-yyyy"
                        dateFormat="dd-MM-yyyy"
                        selected={watch("missing_tooth_date")}
                      />
                    </Col>

                    <Col xs={12} sm={6} xl={2} className="mt-2">
                      <Controller
                        name="missing_reason"
                        control={control}
                        rules={{ required: missingTooth }}
                        render={({ field }) => (
                          <MultiSelect
                            {...field}
                            menuPlacement="auto"
                            closeMenuOnSelect={false}
                            isMulti={false}
                            isClearable
                            options={missingTeethListReason?.map((item) => ({
                              value: item.msngtoothcde_code,
                              label: item.msngtoothcde_name,
                            }))}
                            value={
                              field.value
                                ? {
                                    value: field.value.msngtoothcde_code,
                                    label: field.value.msngtoothcde_name,
                                  }
                                : null
                            }
                            onChange={(selected) =>
                              field.onChange(
                                selected
                                  ? {
                                      msngtoothcde_code: selected.value,
                                      msngtoothcde_name: selected.label,
                                    }
                                  : null
                              )
                            }
                            placeholder="Choose ..."
                          />
                        )}
                      />
                    </Col>
                  </>
                )}
              </Row>
            </Col>
          </Row>

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                reset();
                onHide();
              }}
              className="me-2"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="primary">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditMedicalInformationModal;
